import React, { useState } from 'react';
import { FlatList, Pressable, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import type { EmployeeSummary, ID } from '@hrms/shared';
import { api } from '../../../lib/api';
import { useDebounced } from '../../../lib/hooks';
import { Avatar, Field, Icon, Loading, Modal, Text, TextField, radius, useInputStyle, useTheme } from '../../../ui';

/**
 * Employee picker: a field that opens a searchable list (api.employees.list({ search })).
 * Shows the selected employee's name, fetching it when only the id is known.
 */
export function EmployeePicker({
  value,
  onChange,
  label,
  placeholder = 'Search employee…',
  error,
  hint,
  required,
  status,
  disabled,
  clearable,
  testID,
}: {
  value: ID | null | undefined;
  onChange: (id: ID | null, emp?: EmployeeSummary) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** filter by employee status (e.g. 'active') */
  status?: string;
  disabled?: boolean;
  clearable?: boolean;
  testID?: string;
}) {
  const { colors } = useTheme();
  const base = useInputStyle(!!error, disabled);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<EmployeeSummary | null>(null);
  const dq = useDebounced(text, 250);

  const list = useQuery({
    queryKey: ['employees', 'picker', dq, status],
    queryFn: () => api.employees.list({ search: dq || undefined, per_page: 20, status }),
    enabled: open,
  });
  const known = useQuery({
    queryKey: ['employee', value],
    queryFn: () => api.employees.get(value as ID),
    enabled: !!value && picked?.id !== value,
  });
  const current: EmployeeSummary | null = !value ? null : picked?.id === value ? picked : known.data ?? null;
  const shown = list.data?.data ?? [];

  const pick = (e: EmployeeSummary) => {
    setPicked(e);
    onChange(e.id, e);
    setOpen(false);
    setText('');
  };

  return (
    <Field label={label} required={required} error={error} hint={hint}>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={`${label ?? 'Employee'}: ${current ? current.full_name : placeholder}`}
        disabled={disabled}
        onPress={() => setOpen(true)}
        style={[base, { flexDirection: 'row', alignItems: 'center', gap: 8 }]}
      >
        <Icon name="search" size={15} color={colors.muted} />
        <Text numberOfLines={1} style={{ flex: 1 }} color={current ? colors.text : colors.faint}>
          {current ? `${current.full_name} (${current.emp_code})` : value && known.isLoading ? 'Loading…' : placeholder}
        </Text>
        {clearable && !!value && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Clear employee"
            hitSlop={8}
            onPress={() => {
              setPicked(null);
              onChange(null);
            }}
          >
            <Icon name="x" size={14} color={colors.muted} />
          </Pressable>
        )}
      </Pressable>
      <Modal open={open} onClose={() => setOpen(false)} title={label ?? 'Select employee'} width={480} noScroll>
        <View style={{ padding: 12, paddingBottom: 4 }}>
          <TextField placeholder="Name or employee code…" value={text} onChangeText={setText} autoFocus accessibilityLabel="Search employees" testID="employee-picker-search" />
        </View>
        {list.isLoading ? (
          <Loading />
        ) : (
          <FlatList
            data={shown}
            keyExtractor={(e) => String(e.id)}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{ padding: 8 }}
            keyboardShouldPersistTaps="handled"
            renderItem={({ item }) => {
              const sel = item.id === value;
              return (
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ selected: sel }}
                  onPress={() => pick(item)}
                  style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10, paddingVertical: 9, borderRadius: radius.md, backgroundColor: sel ? colors.primarySoft : pressed ? colors.hover : 'transparent' })}
                >
                  <Avatar name={item.full_name} size={28} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text weight="medium" numberOfLines={1}>
                      {item.full_name}
                    </Text>
                    <Text variant="small" numberOfLines={1}>
                      {[item.emp_code, item.designation, item.department].filter(Boolean).join(' · ')}
                    </Text>
                  </View>
                  {sel && <Icon name="check" size={16} color={colors.primary} />}
                </Pressable>
              );
            }}
            ListEmptyComponent={<Text variant="muted" style={{ padding: 12 }}>No employees found</Text>}
          />
        )}
      </Modal>
    </Field>
  );
}

/** Name + code cell for tables. */
export function PersonCell({ emp, fallback }: { emp?: EmployeeSummary | null; fallback?: string | number }) {
  if (!emp) return <Text>{fallback == null ? '—' : String(fallback)}</Text>;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <Avatar name={emp.full_name} size={26} />
      <View style={{ flexShrink: 1 }}>
        <Text weight="medium" numberOfLines={1}>
          {emp.full_name}
        </Text>
        <Text variant="small" numberOfLines={1}>
          {emp.emp_code}
        </Text>
      </View>
    </View>
  );
}
